import React, { useMemo } from 'react';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import Close from '@mui/icons-material/Close';
import MinimizeViewIcon from '../../../components/CustomSVGIcons/MinimizeViewIcon';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import {
    closeEphemeralMessageUi,
    setMessagesExpandedReturnTarget,
    toggleAvatarMessageModal,
    toggleFloatingMessageModal,
    toggleIsMessageModalExpanded,
} from '../../../redux/appConfigurator/action';
import {
    isMessageModalExpandedSelector,
    messagesExpandedReturnTargetSelector,
} from '../../../redux/appConfigurator/selectors';
import { batch, useDispatch, useSelector } from 'react-redux';
import { alpha } from '@mui/material/styles';
import { createFullName, getJWtDetails } from '../../../utils/AppUtils';
import { ThspianAvatar } from '../../../components/Avatar/ThspianAvatar';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import { SMALL_MOBILE_MEDIA_QUERY } from '../../../components/FloatingChat/floatingMessageUiConstants';

interface MessageHeaderProps {
    /** When true, no line under the header (e.g. empty “select a conversation” pane). */
    hideBottomBorder?: boolean;
}

const MessageHeader: React.FC<MessageHeaderProps> = ({ hideBottomBorder }) => {
    const { themeStyle } = useThemeContext();
    const isSmallMobile = useMediaQuery(SMALL_MOBILE_MEDIA_QUERY);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isExpanded = useSelector(isMessageModalExpandedSelector);
    const messagesExpandedReturnTarget = useSelector(messagesExpandedReturnTargetSelector);
    const { user_id, user, tokenExists } = getJWtDetails();

    /**
     * Self "online" in the header: socket presence (`messages.onlineUsers` via `SET_ONLINE_USERS` in
     * `messages/saga.ts`) is not reliable for the current client (servers often omit self from the list).
     * We show online whenever the user is authenticated; use grey only when not signed in.
     */
    const isCurrentUserOnline = useMemo(() => Boolean(tokenExists && user_id), [tokenExists, user_id]);

    const displayName = useMemo(() => {
        if (!user) return '';
        return createFullName(user).trim() || user.userName || 'You';
    }, [user]);

    const handleCollapse = () => {
        const target = messagesExpandedReturnTarget;
        const pre = sessionStorage.getItem('preMessagesRoute');

        batch(() => {
            dispatch(toggleIsMessageModalExpanded(false));
            dispatch(setMessagesExpandedReturnTarget(undefined));
            if (target === 'avatar-panel') {
                dispatch(toggleFloatingMessageModal(false));
                if (!isSmallMobile) {
                    dispatch(toggleAvatarMessageModal(true));
                }
            } else if (target === 'floating-list') {
                dispatch(toggleFloatingMessageModal(true));
                dispatch(toggleAvatarMessageModal(false));
            }
        });

        if (pre && !pre.startsWith(ROUTES.MESSAGES)) {
            navigate(pre);
        } else {
            navigate(-1);
        }
    };

    const handleCloseModal = () => {
        const pre = sessionStorage.getItem('preMessagesRoute');
        batch(() => {
            dispatch(closeEphemeralMessageUi());
            dispatch(toggleIsMessageModalExpanded(false));
        });
        if (pre && !pre.startsWith(ROUTES.MESSAGES)) {
            navigate(pre);
        } else {
            navigate(-1);
        }
        sessionStorage.removeItem('preMessagesRoute');
    };

    const onSurfaceVariant = theme[themeStyle].grey;
    const iconHoverSx = {
        color: onSurfaceVariant,
        '&:hover': {
            color: theme[themeStyle].highlight,
            bgcolor: 'transparent',
        },
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 2, sm: 3 },
                py: 1.5,
                minHeight: 64,
                borderBottom: hideBottomBorder ? 'none' : `1px solid ${alpha(theme[themeStyle].grey, 0.12)}`,
                bgcolor:
                    themeStyle === 'light'
                        ? alpha(theme[themeStyle].tag_white, 0.72)
                        : alpha(theme[themeStyle].true_black, 0.55),
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: 0,
                    flex: 1,
                }}>
                <Box
                    sx={{ position: 'relative', flexShrink: 0 }}
                    aria-label={isCurrentUserOnline ? 'Online' : 'Offline'}
                    role="img">
                    {user ? (
                        <ThspianAvatar user={user} size="large" withMonogram sx={{ width: 44, height: 44 }} />
                    ) : (
                        <Box
                            sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: theme[themeStyle].deep_grey }}
                        />
                    )}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            width: 11,
                            height: 11,
                            borderRadius: '50%',
                            border: `2px solid ${alpha(theme[themeStyle].secondaryBg, 0.95)}`,
                            bgcolor: isCurrentUserOnline
                                ? theme[themeStyle].green_status_indicator_full
                                : theme[themeStyle].grey,
                        }}
                    />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].tag_white,
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            lineHeight: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                        {displayName}
                    </Typography>
                    {user?.userName ? (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].grey,
                                fontSize: '0.75rem',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            @{user.userName}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {isExpanded && messagesExpandedReturnTarget && !isSmallMobile ? (
                    <IconButton onClick={handleCollapse} aria-label="Collapse messages" size="medium" sx={iconHoverSx}>
                        <MinimizeViewIcon width={24} height={24} stroke="currentColor" />
                    </IconButton>
                ) : null}
                <IconButton
                    onClick={handleCloseModal}
                    aria-label="Close messages"
                    size="medium"
                    sx={{
                        ...iconHoverSx,
                    }}>
                    <Close sx={{ fontSize: 24 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default MessageHeader;
